/**
 * South African ID Number Validator
 * Provides validation utilities for South African ID numbers
 */
export default class SAIDValidator {
  private static readonly idNumberLength = 13;
  private static readonly idNullError = "ID Number cannot be null or empty";
  private static readonly idLengthError = `ID Number must be ${SAIDValidator.idNumberLength} digits long`;
  private static readonly nonNumericCharactersError = "ID Number must contain only numeric characters";
  private static readonly invalidDatePartError = "Invalid Date part for SA ID Number";
  private static readonly incorrectCheckDigitError = "ID Number has an incorrect check digit";

  /**
   * Validates a South African ID number
   * @param idNumber The ID number to validate
   * @param options Validation options
   * @returns Validation result with isValid flag and error message
   */
  public static checkIfIdNumberIsValid(
    idNumber: string,
    options: { allowSpaces?: boolean; disableControlDigitCheck?: boolean; minimumAgeInYears?: number } = {}
  ): { isValid: boolean; errorString: string } {
    if (!idNumber) idNumber = '';
    if (options.allowSpaces) {
      idNumber = idNumber.replace(/\s+/g, '');
    }
    if (!idNumber) {
      return { isValid: false, errorString: this.idNullError };
    }
    if (!/^\d+$/.test(idNumber)) {
      return { isValid: false, errorString: this.nonNumericCharactersError };
    }
    if (idNumber.length !== this.idNumberLength) {
      return {
        isValid: false,
        errorString: this.idLengthError,
      };
    }

    const birthDate = this.tryGetBirthDateFromSAIdNumber(idNumber, options.minimumAgeInYears ?? 0);

    if (birthDate.errorString && birthDate.errorString.trim() !== '') {
      return { isValid: false, errorString: birthDate.errorString };
    }

    if (!options.disableControlDigitCheck) {
      const controlDigit = this.getControlDigit(idNumber);
      if (parseInt(idNumber[12], 10) !== controlDigit) {
        return { isValid: false, errorString: this.incorrectCheckDigitError };
      }
    }
    return { isValid: true, errorString: '' };
  }

  /**
   * Attempts to extract and validate the birth date from a South African ID number
   * @param saIdNumber The South African ID number
   * @param minimumAgeInYears Minimum required age (default: 0)
   * @returns Object containing the birth date (if valid) and any error message
   */
  public static tryGetBirthDateFromSAIdNumber(
    saIdNumber: string,
    minimumAgeInYears: number = 0
  ): { dateOfBirth: Date | null; errorString: string } {
    try {
      if (!saIdNumber) {
        return { dateOfBirth: null, errorString: this.idNullError };
      }

      if (saIdNumber.length !== this.idNumberLength) {
        return {
          dateOfBirth: null,
          errorString: this.idLengthError,
        };
      }

      const day = parseInt(saIdNumber.substr(4, 2), 10);
      const month = parseInt(saIdNumber.substr(2, 2), 10);
      const yearPrefix = new Date().getFullYear().toString().substr(0, 2);
      let year = parseInt(yearPrefix + saIdNumber.substr(0, 2), 10);

      let dateOfBirth = new Date(year, month - 1, day);

      // Strict calendar validation before century adjustment
      if (
        day < 1 ||
        day > 31 ||
        month < 1 ||
        month > 12 ||
        isNaN(dateOfBirth.getTime()) ||
        dateOfBirth.getFullYear() !== year ||
        dateOfBirth.getMonth() !== month - 1 ||
        dateOfBirth.getDate() !== day
      ) {
        return { dateOfBirth: null, errorString: this.invalidDatePartError };
      }

      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - minimumAgeInYears);

      if (dateOfBirth > minDate) {
        dateOfBirth.setFullYear(dateOfBirth.getFullYear() - 100);
      }

      return { dateOfBirth, errorString: '' };
    } catch {
      return { dateOfBirth: null, errorString: this.invalidDatePartError };
    }
  }

  /**
   * Calculates the control (check) digit for a South African ID number
   * @param idNumber The first 12 digits of the ID number
   * @returns The calculated control digit
   */
  private static getControlDigit(idNumber: string): number {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      let digit = parseInt(idNumber[i], 10);
      if ((i + 1) % 2 === 0) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    return (10 - (sum % 10)) % 10;
  }
}
